import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";

import { defaultIfEmpty, filter, lastValueFrom } from "rxjs";

import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import { ImportRiskAssessmentTemplateDialogComponent } from "@components/shared/risk-assessment-templates/ui";
import { validResourceType } from "@components/shared/risk-assessment-templates/utils";
import { TextConstants } from "@shared/constants";
import { RecordStateEnum, RouteEnum } from "@shared/enums";
import { AuthenticationService } from "@shared/services";
import { RouterService } from "@shared/services/router.service";

@Component({
  standalone: false,
  selector: "app-settings-risk-assessment-templates",
  templateUrl: "./settings-risk-assessment-templates.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsRiskAssessmentTemplatesComponent implements OnInit {
  private readonly dialog = inject(MatDialog);

  private readonly router = inject(RouterService);

  private readonly authService = inject(AuthenticationService);

  readonly hasEntitiesWriteAccess = this.authService.hasEntitiesWriteAccess;

  readonly recordState = RecordStateEnum;

  readonly resourceType = RiskAssessmentTemplateResourceType;

  readonly routing = RouteEnum;

  readonly selectedTabIndex = signal(0);

  readonly translations = {
    title: $localize`:@@shared.riskassessmenttemplates:Risk assessment templates`,
    titleBackText: TextConstants.BACK_TO_ADMIN,
    addNewLabel: TextConstants.ADD_NEW,
  };

  private readonly resourceTypeMap = {
    0: RiskAssessmentTemplateResourceType.COUNTRY,
    1: RiskAssessmentTemplateResourceType.ORGANISATION,
    2: RiskAssessmentTemplateResourceType.LOCATION,
    3: RiskAssessmentTemplateResourceType.MATERIAL,
    4: RiskAssessmentTemplateResourceType.PRODUCT,
    5: RiskAssessmentTemplateResourceType.DELIVERY,
  };

  private get selectedResourceType() {
    return this.resourceTypeMap[this.selectedTabIndex()];
  }

  ngOnInit() {
    const resourceType = this.router.getQueryParams()["type"];

    if (validResourceType(resourceType)) {
      this.selectedTabIndex.set(Object.values(this.resourceTypeMap).indexOf(resourceType));
    }
  }

  onAdd(isImported?: boolean) {
    const params = {
      type: this.selectedResourceType,
      ...(isImported && { imported: true }),
    };

    this.router.navigate(this.router.getRiskAssessmentTemplateLink(undefined, true, params));
  }

  async openImportDialog() {
    const dialogRef = this.dialog.open(ImportRiskAssessmentTemplateDialogComponent, {
      width: "550px", // needs to be wider to fit french
    });

    const result = await lastValueFrom(
      dialogRef.afterClosed().pipe(filter(Boolean), defaultIfEmpty(null)),
    );

    if (result) {
      this.onAdd(true);
    }
  }
}
